/**
 * Turning a Twitch clip link into something that will actually play.
 *
 * topstarrr: "möglichkeit bei sandbox einbettung von videos twitch clips zu
 * ziehen weil die kanns irgendwie nich". The sandbox renders a plain <video>
 * tag, which handles an uploaded MP4 and nothing else — a Twitch clip is not a
 * video file, it is an embed, so pasting a clip link there could only ever
 * produce a black rectangle.
 *
 * Two details make clip embeds fail silently for people who try it by hand:
 * the URL a viewer copies is not the URL that embeds, and Twitch refuses to
 * play an embed whose `parent` does not match the hosting page. Both live here
 * so there is one answer rather than one per caller.
 */
/** Clip slugs are Twitch-generated words; anything else is not a slug. */
const SLUG_SHAPE = /^[A-Za-z0-9_-]{4,100}$/;
/**
 * Extracts the clip slug from whatever a person pasted.
 *
 * Handles the share link (`clips.twitch.tv/Slug`), the channel link
 * (`twitch.tv/<kanal>/clip/Slug`), an embed link that is already correct, and
 * a bare slug. Returns null for anything that is not a clip, so callers can
 * fall back to treating the value as an ordinary video file.
 */
export function parseTwitchClipSlug(input) {
    const value = input.trim();
    if (!value)
        return null;
    // Not a URL at all — accept it only if it looks like a slug.
    if (!/^https?:\/\//i.test(value)) {
        return SLUG_SHAPE.test(value) ? value : null;
    }
    // Parsed by hand rather than with URL/URLSearchParams: this package is
    // consumed by both the browser bundle and the Node backend and pulls in
    // neither DOM nor Node type libraries, so those globals do not exist here.
    const match = /^https?:\/\/([^/?#]+)([^?#]*)(?:\?([^#]*))?/i.exec(value);
    if (!match)
        return null;
    const host = match[1].split(":")[0].replace(/^www\./i, "").toLowerCase();
    if (host !== "clips.twitch.tv" && host !== "twitch.tv" && host !== "m.twitch.tv")
        return null;
    // Already an embed URL: the slug sits in the query string.
    const query = match[3] ?? "";
    const fromQuery = /(?:^|&)clip=([^&]*)/.exec(query)?.[1];
    if (fromQuery && SLUG_SHAPE.test(fromQuery))
        return fromQuery;
    const parts = (match[2] ?? "").split("/").filter(Boolean);
    // https://clips.twitch.tv/<slug>
    if (host === "clips.twitch.tv" && parts.length === 1 && SLUG_SHAPE.test(parts[0])) {
        return parts[0];
    }
    // https://www.twitch.tv/<kanal>/clip/<slug>
    const clipIndex = parts.indexOf("clip");
    if (clipIndex >= 0 && parts[clipIndex + 1] && SLUG_SHAPE.test(parts[clipIndex + 1])) {
        return parts[clipIndex + 1];
    }
    return null;
}
/**
 * Builds the embed URL for a clip slug.
 *
 * Every `parent` is sent as its own parameter — Twitch expects repetition, not
 * a comma-separated list, and gets this wrong quietly by showing an empty
 * player instead of an error.
 */
export function buildClipEmbedUrl(slug, options) {
    const params = [
        `clip=${encodeURIComponent(slug)}`,
        `autoplay=${options.autoplay === false ? "false" : "true"}`,
        `muted=${options.muted ? "true" : "false"}`,
    ].join("&");
    const parents = options.parents.filter(Boolean).filter((p, i, all) => all.indexOf(p) === i);
    const parentQuery = parents.map((p) => `&parent=${encodeURIComponent(p)}`).join("");
    return `https://clips.twitch.tv/embed?${params}${parentQuery}`;
}
/** Convenience for callers that only have the pasted string and a host. */
export function twitchClipEmbedFrom(input, options) {
    const slug = parseTwitchClipSlug(input);
    return slug ? buildClipEmbedUrl(slug, options) : null;
}
//# sourceMappingURL=twitch-clips.js.map